import React, { useLayoutEffect, useRef, useState } from 'react';
import { fileUrl } from '@/lib/api';
import { useReveal } from '@/lib/motion';

// Upcoming Events — homepage, between Hero and Experiences.
//
// Data arrives on the main /public/home payload (never a late client
// fetch), so this section renders with the page and can never insert
// itself after load and shift the anchor destinations below it. With
// zero visible events Home.js does not mount this component at all,
// and the render guard below returns null defensively — no heading,
// no divider, no empty space.
//
// Cards flip: front shows image / name / date, back shows description,
// location and the status/CTA. Flip state is REACT STATE in every
// input mode — hover on hover-capable pointers is implemented with
// mouseenter/mouseleave that set the same state the keyboard buttons
// set, so aria-expanded and aria-hidden are always truthful, never a
// CSS-only :hover that silently disagrees with the accessibility tree.
// The flip classes are event-specific (.event-card-*); nothing here
// touches or depends on the Experiences .exp-card-* classes.

// Same rule as the backend validator: really parse it, and require an
// http(s) scheme AND a nonempty hostname.
const isValidRegistrationUrl = (u) => {
  try {
    const p = new URL(u);
    return (p.protocol === 'http:' || p.protocol === 'https:') && p.hostname.length > 0;
  } catch (e) {
    return false;
  }
};

// True on devices whose PRIMARY pointer both hovers and is fine
// (mouse/trackpad desktops). Touch phones/tablets report hover:none,
// so their taps fall through to the click-toggle path instead.
const isHoverCapable = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: hover) and (pointer: fine)').matches;

export default function UpcomingEventsSection({ events = [] }) {
  // HOOK ORDER: every hook stays above the early return below — a hook
  // after a conditional return crashes the component (the codebase's
  // documented SpeakerDetail bug). Home.js only mounts this component
  // when events exist, so the reveal observers attach to real DOM.
  const headerReveal = useReveal();
  const gridReveal = useReveal({ threshold: 0.05 });
  // Single shared flipped slot (Experiences conventions): flipping one
  // card flips any other back, so only one back face shows at a time.
  const [flippedId, setFlippedId] = useState(null);

  if (!Array.isArray(events) || events.length === 0) return null;

  return (
    <section id="upcoming-events" className="section-pad relative bg-bg-canvas" data-testid="section-upcoming-events">
      <div className="container-x">
        <div
          ref={headerReveal.ref}
          className={`text-center ${headerReveal.simple ? 'reveal-simple' : 'reveal-up'} ${headerReveal.revealed ? 'is-in' : ''}`}
        >
          <h2
            className="font-display font-bold uppercase text-ink-primary leading-[1.05] tracking-tightish text-[36px] md:text-[40px] lg:text-[56px]"
            data-testid="upcoming-events-headline"
          >
            Upcoming Events
          </h2>
        </div>

        <div
          ref={gridReveal.ref}
          className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="upcoming-events-grid"
        >
          {events.map((ev, idx) => (
            <EventCard
              key={ev.id || idx}
              ev={ev}
              idx={idx}
              isFlipped={flippedId === (ev.id || `evt-idx-${idx}`)}
              cardId={ev.id || `evt-idx-${idx}`}
              setFlippedId={setFlippedId}
              revealed={gridReveal.revealed}
              simple={gridReveal.simple}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function EventCard({ ev, idx, cardId, isFlipped, setFlippedId, revealed, simple }) {
  const frontBtnRef = useRef(null);
  const backBtnRef = useRef(null);
  const backFaceRef = useRef(null);

  const image = fileUrl(ev.image) || ev.image;
  const url = (ev.registration_url || '').trim();
  const hasUrl = isValidRegistrationUrl(url);
  const statusText = (ev.status_label || '').trim();
  const delay = idx * 80;

  const focusSafely = (ref) => {
    if (ref.current && typeof ref.current.focus === 'function') {
      try {
        ref.current.focus({ preventScroll: true });
      } catch (e) {
        ref.current.focus();
      }
    }
  };

  // ONE focus authority: this LAYOUT effect runs on every flip-state
  // change, from ANY cause — keyboard, tap, hover onto this card, or
  // another card's hover stealing the single flipped slot. If the face
  // that just went inactive (aria-hidden, tabIndex -1) contains
  // document.activeElement, focus is moved to the matching control on
  // the face that is now visible, so keyboard focus can never be
  // stranded on a hidden element. useLayoutEffect is REQUIRED here: it
  // runs synchronously in the same commit that applied aria-hidden,
  // BEFORE the browser paints or updates the accessibility tree — a
  // passive useEffect would let assistive technology observe focus
  // sitting inside a hidden face for a frame (Chrome logs a
  // retained-focus/aria-hidden warning for exactly that). There are
  // deliberately NO requestAnimationFrame focus transfers left in this
  // component — the effect always reads the CURRENT state, so a stale
  // queued transfer cannot exist.
  useLayoutEffect(() => {
    if (isFlipped) {
      if (frontBtnRef.current && frontBtnRef.current === document.activeElement) {
        focusSafely(backBtnRef);
      }
    } else if (backFaceRef.current && backFaceRef.current.contains(document.activeElement)) {
      focusSafely(frontBtnRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFlipped]);

  // Flip controls just set state; the effect above owns focus.
  const flipToBack = () => setFlippedId(cardId);
  const flipToFront = () => setFlippedId(null);

  // Hover path — same state (aria stays truthful). Guarded to
  // hover-capable pointers so touch-emulated mouse events on phones
  // change nothing; any needed focus rescue happens in the effect.
  const onMouseEnter = () => {
    if (isHoverCapable()) setFlippedId(cardId);
  };
  const onMouseLeave = () => {
    if (isHoverCapable()) setFlippedId((prev) => (prev === cardId ? null : prev));
  };

  return (
    <div
      className={`${simple ? 'reveal-simple' : 'reveal-card'} ${revealed ? 'is-in' : ''}`}
      style={{ transitionDelay: revealed ? `${delay}ms` : '0ms' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid="upcoming-event-card"
      data-flipped={isFlipped ? 'true' : 'false'}
    >
      <div className="event-card-3d relative rounded-md bg-bg-elevated aspect-[4/3] md:aspect-[3/4]">
        <div className={`event-card-flip-inner ${isFlipped ? 'is-flipped' : ''}`}>
          {/* Front face: image, name, date. The flip control is a real
              <button> overlay (keyboard-activatable), and this face —
              button included — leaves the accessibility tree and tab
              order while the back is showing. */}
          <div className="event-card-face event-card-face-front bg-bg-elevated" aria-hidden={isFlipped} data-testid="upcoming-event-front">
            {image ? (
              <img src={image} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-90" />
            ) : (
              <div className="absolute inset-0 bg-card-dark" aria-hidden="true" />
            )}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0) 30%, rgba(0,0,0,0.85) 100%)' }} />
            <div className="absolute inset-0 flex flex-col justify-end p-6 z-[1] pointer-events-none">
              <h3 className="font-display font-bold uppercase text-white text-[20px] leading-[1.1] tracking-tightish" data-testid="upcoming-event-name">
                {ev.name}
              </h3>
              <p className="mt-2 font-body font-semibold text-[13px] uppercase tracking-wider2 text-brand-orange" data-testid="upcoming-event-date">
                {ev.date}
              </p>
            </div>
            <button
              ref={frontBtnRef}
              type="button"
              onClick={flipToBack}
              tabIndex={isFlipped ? -1 : 0}
              aria-expanded={isFlipped}
              aria-label={`Show details for ${ev.name}`}
              className="event-card-front-btn absolute inset-0 z-[2] rounded-md focus:outline-none"
              data-testid="upcoming-event-flip"
            />
          </div>

          {/* Back face: description (scrollable), location, status/CTA,
              and a real flip-back button so keyboard users can return.
              Hidden from AT and the tab order while the front shows. */}
          <div ref={backFaceRef} className="event-card-face event-card-face-back bg-card-dark" aria-hidden={!isFlipped} data-testid="upcoming-event-back">
            <div className="event-card-back-scroll absolute inset-0">
              <div className="event-card-back-body p-6 flex flex-col min-h-full">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display font-bold uppercase text-white text-[18px] leading-[1.1] tracking-tightish">
                    {ev.name}
                  </h3>
                  <button
                    ref={backBtnRef}
                    type="button"
                    onClick={flipToFront}
                    tabIndex={isFlipped ? 0 : -1}
                    aria-label={`Show ${ev.name} event cover`}
                    className="shrink-0 font-body font-semibold text-[11px] uppercase tracking-wider2 text-white/70 hover:text-white border border-white/30 rounded-sm px-2 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
                    data-testid="upcoming-event-flip-back"
                  >
                    Back
                  </button>
                </div>
                <p className="mt-3 font-body text-[13px] text-white/85 leading-relaxed" data-testid="upcoming-event-description">
                  {ev.description}
                </p>
                <p className="mt-3 font-body font-semibold text-[12px] uppercase tracking-wider2 text-white/70" data-testid="upcoming-event-location">
                  {ev.location}
                </p>
                <div className="mt-auto pt-4">
                  {hasUrl ? (
                    // status_label is the CTA wording when a link exists
                    // (defensive "Register Now" if it is somehow blank).
                    // Anchor and flip button are SIBLINGS in the tree —
                    // never an anchor nested inside a button.
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      tabIndex={isFlipped ? 0 : -1}
                      className="btn-primary text-[12px] !py-2.5 !px-5 inline-block"
                      data-testid="upcoming-event-cta"
                    >
                      {statusText || 'Register Now'}
                    </a>
                  ) : statusText ? (
                    // No link: the status is plain text — never an empty
                    // anchor or a disabled fake button.
                    <span className="font-body font-bold text-[12px] uppercase tracking-wider2 text-brand-orange" data-testid="upcoming-event-status">
                      {statusText}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
